/** @jsxImportSource @emotion/react */
import { Typography } from '@quickbit/qb-design-system';
import { useIntl } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAsync, useAsyncFn, useUpdateEffect } from 'react-use';
import { ErrorComponent } from 'components';
import { useMixpanel } from 'context';
import { getReceiveOrderDetails } from 'onramp-pages/api';
import { OnrampLayout } from 'onramp-pages/components';
import { useOnrampData, useSetOnrampData } from '../context';
import { createSendPreview, sendOrder } from './api';

export const Preview = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const setOnrampData = useSetOnrampData();

  const { receiveOrderPreviewId, merchantName, autoProcess } = useOnrampData();

  const { mixpanelTrackEvent } = useMixpanel({
    Product: 'TopUp',
    Page: 'Preview',
  });

  const { value: initialStatus } = useAsync(async () => {
    const details = await getReceiveOrderDetails(receiveOrderPreviewId);
    return details.status;
  });

  const [{ value, loading, error }, createSend] = useAsyncFn(async () => {
    const preview = await createSendPreview(receiveOrderPreviewId);
    mixpanelTrackEvent('Send started', {
      description: 'User started send preview (review) step.',
    });
    setOnrampData({
      cryptoAmount: preview.amount,
    });
    return preview;
  }, [receiveOrderPreviewId]);

  const [{ error: sendError, loading: sendLoading }, executeSend] = useAsyncFn(
    async (sendPreviewId: string) => {
      const sendOrderResponse = await sendOrder(sendPreviewId);
      mixpanelTrackEvent('Processing started', {
        description:
          'User started the send order processing step. This means that the user already had crypto.',
      });
      navigate(`/onramp/processing`);
      return sendOrderResponse;
    }
  );

  useUpdateEffect(() => {
    if (initialStatus) {
      mixpanelTrackEvent('Initial Status loaded', {
        description: `The user landed on the Preview page with the status ${initialStatus}`,
      });
      if (
        ['COMPLETE', 'CRYPTO_RECEIVED', 'CRYPTO_SOLD'].includes(initialStatus)
      ) {
        navigate(`/onramp/completed`);
        return;
      }
      if (initialStatus === 'CANCELLED') {
        navigate(`/onramp/cancelled`);
        return;
      }
      if (['ADDRESS_CREATED', 'BUY_INITIATED'].includes(initialStatus)) {
        void createSend().then((r) => {
          if (autoProcess && r && r.id) {
            mixpanelTrackEvent('Send clicked', {
              autoProcess: true,
              description: 'User completed the order preview step.',
            });
            void executeSend(r.id);
          }
        });
        return;
      }
      navigate(`/onramp/processing`);
    }
  }, [
    initialStatus,
    createSend,
    navigate,
    mixpanelTrackEvent,
    autoProcess,
    executeSend,
  ]);

  if (sendError || error) {
    const errorCode = error?.errorCode ?? sendError?.errorCode;
    if (errorCode === 'COMPLETED_PREVIEW') {
      return <Navigate to={`/onramp/completed`} />;
    }
    if (errorCode === 'INSUFFICIENT_FUNDS') {
      return <Navigate to={`/onramp/buy`} />;
    }
    if (errorCode === 'DELETED_PREVIEW') {
      return <Navigate to={`/onramp/cancelled`} />;
    }
    return (
      <ErrorComponent
        error={error ?? sendError}
        tryAgainAction={() => navigate(-1)}
      />
    );
  }

  return (
    <OnrampLayout
      buttonProps={{
        text: formatMessage({
          id: 'page.onramp.send.send',
        }),
        disabled: !value || loading || sendLoading,
        onClick: () => {
          if (!value) return;
          mixpanelTrackEvent('Send clicked', {
            description: 'User completed the order preview step.',
          });
          void executeSend(value.id);
        },
        loading: !value || sendLoading || loading,
      }}
      topChildren={
        <>
          <Typography variant="body">
            {formatMessage({ id: 'page.onramp.send.title' }, { merchantName })}
          </Typography>
        </>
      }
    ></OnrampLayout>
  );
};

export default Preview;
